// src/pages/HcvShortfallPage.js
// UNLINKED public page: Housing Choice Voucher shortfall tracker for every
// HCV agency in Massachusetts. Data is HUD's own (the Two-Year Tool workbook,
// re-cut per agency by the compliance service's src/hcv engine). Map + state
// totals at the top, a searchable list of agencies below; each row opens
// /hcv-shortfall/:slug (agency name, e.g. /hcv-shortfall/salem) with the full profile.
//
// Route: /hcv-shortfall — not linked from the nav, footer, or any hub.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import Brand from '../components/Brand';
import { COMPLIANCE_API, useIsMobile, T, BUCKETS, BUCKET_ORDER, fmt, BucketChip, Header, Card, Stat } from './hcvShortfallShared';

const STATE = 'MA';
const LEAFLET_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
const LEAFLET_JS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = LEAFLET_CSS; link.crossOrigin = '';
    document.head.appendChild(link);
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = LEAFLET_JS; s.async = true; s.crossOrigin = '';
    s.onload = () => resolve(window.L);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

const SORTS = [
  { id: 'risk', label: 'Risk (default)' },
  { id: 'zero', label: 'Runs out soonest' },
  { id: 'units', label: 'Most vouchers' },
  { id: 'burn', label: 'Spending vs funding' },
  { id: 'util', label: 'Lowest leasing' },
  { id: 'reserves', label: 'Fewest reserve months' },
  { id: 'name', label: 'Name A–Z' },
];

function sortRows(rows, sort) {
  const order = Object.fromEntries(BUCKET_ORDER.map((b, i) => [b, i]));
  const num = (v, fallback) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
  const r = [...rows];
  switch (sort) {
    case 'zero': r.sort((a, b) => num(a.zeroIn, 1e9) - num(b.zeroIn, 1e9) || num(b.leasedLatest, 0) - num(a.leasedLatest, 0)); break;
    case 'units': r.sort((a, b) => num(b.leasedLatest, -1) - num(a.leasedLatest, -1)); break;
    case 'burn': r.sort((a, b) => num(b.burnRatio, -1) - num(a.burnRatio, -1)); break;
    case 'util': r.sort((a, b) => num(a.unitUtil, 2) - num(b.unitUtil, 2)); break;
    case 'reserves': r.sort((a, b) => num(a.reserveMonths, 1e9) - num(b.reserveMonths, 1e9)); break;
    case 'name': r.sort((a, b) => a.name.localeCompare(b.name)); break;
    default: r.sort((a, b) => order[a.bucket] - order[b.bucket] || num(b.leasedLatest, 0) - num(a.leasedLatest, 0));
  }
  return r;
}

export default function HcvShortfallPage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [bucket, setBucket] = useState('all');
  const [hudOnly, setHudOnly] = useState(false);
  const [sort, setSort] = useState('risk');
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    document.title = 'HCV Shortfall Tracker · Massachusetts · HousingAI';
    let cancelled = false;
    fetch(`${COMPLIANCE_API}/api/hcv/states/${STATE}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => (data ? data.phas : []), [data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortRows(rows.filter((r) =>
      (bucket === 'all' || r.bucket === bucket) &&
      (!hudOnly || /high risk/i.test(r.hudStatus || '')) &&
      (!q || `${r.name} ${r.city || ''} ${r.code} ${r.county || ''}`.toLowerCase().includes(q))
    ), sort);
  }, [rows, search, bucket, hudOnly, sort]);

  // ---- map ----
  useEffect(() => {
    if (!data || !mapRef.current || mapObj.current) return;
    let disposed = false;
    loadLeaflet().then((L) => {
      if (disposed || !mapRef.current) return;
      const map = L.map(mapRef.current, { scrollWheelZoom: false, zoomControl: !mobile, attributionControl: true });
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 18,
      }).addTo(map);
      map.fitBounds([[41.2, -73.55], [42.9, -69.9]]);
      mapObj.current = map;
      layerRef.current = L.layerGroup().addTo(map);
      setTick((t) => t + 1);
    }).catch(() => {});
    return () => { disposed = true; };
  }, [data, mobile]);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const L = window.L; const layer = layerRef.current;
    if (!L || !layer) return;
    layer.clearLayers();
    const pts = filtered.filter((r) => r.lat && r.lon && r.bucket !== 'nodata');
    const maxLeased = Math.max(1, ...pts.map((r) => r.leasedLatest || 0));
    pts.slice().sort((a, b) => (b.leasedLatest || 0) - (a.leasedLatest || 0)).forEach((r) => {
      const b = BUCKETS[r.bucket] || BUCKETS.nodata;
      const radius = 6 + 22 * Math.sqrt((r.leasedLatest || 0) / maxLeased);
      const m = L.circleMarker([r.lat, r.lon], { radius, color: '#ffffff', weight: 2, fillColor: b.color, fillOpacity: 0.78 });
      m.bindTooltip(`<strong>${r.name}</strong><br/><span style="color:${b.color};font-weight:700">${b.icon} ${b.label}</span> · ${fmt.int(r.leasedLatest)} vouchers${r.zeroMonthLabel ? ` · runs out ${r.zeroMonthLabel}` : ''}`, { direction: 'top', offset: [0, -radius], opacity: 0.95 });
      m.on('click', () => navigate(`/hcv-shortfall/${r.slug || r.code}`));
      layer.addLayer(m);
    });
  }, [filtered, tick, navigate]);

  const s = data ? data.summary : null;
  const counts = s ? s.counts : {};
  const dataThrough = data ? fmt.month(data.source.dataThrough) : '';

  const th = { textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: T.gray500, fontWeight: 600, padding: '0.6rem 0.75rem', borderBottom: `1px solid ${T.gray200}`, whiteSpace: 'nowrap' };
  const td = { padding: '0.7rem 0.75rem', borderBottom: `1px solid ${T.gray100}`, fontSize: '0.88rem', color: T.gray800, verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums' };
  const num = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

  return (
    <div style={{ fontFamily: T.font, minHeight: '100vh', background: T.gradient, display: 'flex', flexDirection: 'column' }}>
      <Header
        mobile={mobile}
        chip={(
          <>
            <button onClick={() => navigate('/')} title="Back to Home" style={{ background: T.gray100, border: `1px solid ${T.gray200}`, borderRadius: T.radiusPill, cursor: 'pointer', fontSize: '0.85rem', color: T.gray600, padding: '0.4rem 0.8rem', fontFamily: T.font, fontWeight: 600 }}>←</button>
            <Brand as="a" href="https://gethousingai.com" theme="light" markClassName="text-blue-600" style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.05rem' : '1.3rem', fontFamily: T.font, textDecoration: 'none' }} />
            <span style={{ color: T.gray300, fontSize: '1.4rem', fontWeight: 300 }}>|</span>
            <span style={{ fontSize: mobile ? '0.72rem' : '0.85rem', padding: '0.25rem 0.75rem', background: T.blue100, color: T.blue600, borderRadius: T.radiusPill, fontWeight: 600 }}>{mobile ? 'HCV Shortfall Tracker' : 'HCV Shortfall Tracker · Massachusetts'}</span>
          </>
        )}
        right={data ? <>HUD data through <strong>{dataThrough}</strong> · reserves reconciled {fmt.month(data.source.reservesAsOf.slice(0, 7))} · Two-Year Tool extract</> : null}
      />

      <main style={{ flex: 1, width: '100%', maxWidth: '1280px', margin: '0 auto', padding: mobile ? '1rem' : '1.5rem 1.5rem 3rem', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: '1.25rem' }}>
          <h1 style={{ fontSize: mobile ? '1.5rem' : '2rem', fontWeight: 800, color: T.gray900, margin: 0, letterSpacing: '-0.01em' }}>Which Massachusetts voucher programs run out of money first</h1>
          <p style={{ color: T.gray600, margin: '0.5rem 0 0', maxWidth: '62rem', lineHeight: 1.55, fontSize: mobile ? '0.9rem' : '0.98rem' }}>
            Every Housing Choice Voucher agency in the state, profiled from HUD&apos;s published leasing, spending, reserve and funding figures.
            Reserves roll forward month by month at each agency&apos;s trailing burn and per-unit-cost trend; the month the balance crosses zero is the estimate shown.
            Public data lags about four months, so read these as trends, not dates.
          </p>
        </div>

        {error && <Card style={{ color: BUCKETS.shortfall.color }}>Could not load the tracker ({error}). The data service may be waking up; try again in a minute.</Card>}
        {!data && !error && <Card style={{ color: T.gray500 }}>Loading HUD data for {STATE}…</Card>}

        {s && (
          <>
            {/* headline stats */}
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(4, minmax(0,1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Stat big label="Reserves exhausted now" value={fmt.int(counts.exhausted)} accent={BUCKETS.exhausted.color} sub={`of ${s.evaluated} agencies modeled · ${fmt.int(s.familiesInExhaustedAgencies)} households · already spending past their funding`} />
              <Stat big label="Exhausted or within 12 months" value={fmt.pct(s.pctShortfall)} accent={BUCKETS.shortfall.color} sub={`${counts.exhausted + counts.shortfall} agencies · ${fmt.int(s.familiesInShortfallAgencies)} households in those programs`} />
              <Stat big label="At risk (incl. watch)" value={fmt.pct(s.pctAtRisk)} accent={BUCKETS.watch.color} sub={`${counts.exhausted + counts.shortfall + counts.watch} agencies · ${fmt.int(s.familiesAtRisk)} households`} />
              <Stat big label="Underutilized" value={fmt.pct(s.pctUnderutilized)} accent={BUCKETS.underutilized.color} sub={`${counts.underutilized} agencies leaving funding or units unused`} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(6, minmax(0,1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <Stat label="Statewide budget utilization" value={fmt.pct(s.budgetUtil12, 1)} sub="trailing 12 months of HAP ÷ budget authority" />
              <Stat label="Unit utilization" value={fmt.pct(s.unitUtil, 1)} sub={`${fmt.int(s.leased)} of ${fmt.int(s.accUnits)} ACC units leased`} />
              <Stat label="Reserves on hand" value={fmt.usdShort(s.reservesNow)} sub={`${fmt.months(s.burnMonthly ? s.reservesNow / s.burnMonthly : null)} months of HAP at ${fmt.usdShort(s.burnMonthly)}/mo`} />
              <Stat label="On HUD's high-risk list" value={fmt.int(s.hudHighRisk)} sub="Shortfall Prevention Team status" />
              <Stat label="2025 shortfall awards" value={fmt.usdShort(s.shortfallAwards2025)} sub="set-aside funding HUD sent to MA agencies" />
              <Stat label="Below zero at year end" value={fmt.int(s.yearEndNegative)} sub={`combined gap ${fmt.usdShort(s.yearEndGap)} by Dec ${data.source.budgetYear} at trend`} />
            </div>

            {/* filters */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
              {['all', ...BUCKET_ORDER].map((b) => {
                const active = bucket === b;
                const meta = b === 'all' ? { label: 'All agencies', color: T.gray700, bg: T.gray100, icon: '' } : BUCKETS[b];
                const n = b === 'all' ? rows.length : counts[b] || 0;
                return (
                  <button key={b} onClick={() => setBucket(b)} title={meta.blurb || ''} style={{ cursor: 'pointer', fontFamily: T.font, fontWeight: 700, fontSize: '0.8rem', padding: '0.4rem 0.8rem', borderRadius: T.radiusPill, border: `2px solid ${active ? meta.color : 'transparent'}`, background: meta.bg, color: meta.color, display: 'inline-flex', gap: '0.4rem', alignItems: 'center' }}>
                    {meta.icon && <span aria-hidden="true" style={{ fontSize: '0.75em' }}>{meta.icon}</span>}{meta.label} <span style={{ fontWeight: 600 }}>{n}</span>
                  </button>
                );
              })}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: T.gray600, marginLeft: mobile ? 0 : 'auto', cursor: 'pointer' }}>
                <input type="checkbox" checked={hudOnly} onChange={(e) => setHudOnly(e.target.checked)} /> HUD high-risk only
              </label>
            </div>

            {/* map */}
            <Card style={{ padding: 0, overflow: 'hidden', marginBottom: '1.25rem' }}>
              <div ref={mapRef} style={{ height: mobile ? 340 : 520, width: '100%', background: '#eef2f5' }} aria-label="Map of Massachusetts housing authorities by shortfall status" />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', padding: '0.6rem 1rem', fontSize: '0.76rem', color: T.gray600, borderTop: `1px solid ${T.gray100}` }}>
                {BUCKET_ORDER.filter((b) => b !== 'nodata').map((b) => (
                  <span key={b} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: BUCKETS[b].color, display: 'inline-block' }} /> {BUCKETS[b].label}
                  </span>
                ))}
                <span style={{ marginLeft: 'auto' }}>Circle size = vouchers under lease · click a circle for the agency profile</span>
              </div>
            </Card>

            {/* list */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', marginBottom: '0.6rem' }}>
              <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 420 }}>
                <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.gray400 }} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search housing authorities, towns, HUD codes…" style={{ width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.75rem 0.6rem 2.2rem', borderRadius: T.radiusPill, border: `1px solid ${T.gray200}`, fontFamily: T.font, fontSize: '0.9rem', background: T.white, outline: 'none' }} />
              </div>
              <select aria-label="Sort authorities" value={sort} onChange={(e) => setSort(e.target.value)} style={{ padding: '0.55rem 0.75rem', borderRadius: T.radiusPill, border: `1px solid ${T.gray200}`, fontFamily: T.font, fontSize: '0.85rem', background: T.white, color: T.gray700 }}>
                {SORTS.map((o) => <option key={o.id} value={o.id}>Sort: {o.label}</option>)}
              </select>
              <span style={{ fontSize: '0.8rem', color: T.gray500 }}>{filtered.length} of {rows.length} agencies</span>
            </div>

            {mobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {filtered.map((r) => (
                  <Card key={r.code} style={{ cursor: 'pointer', padding: '0.85rem 1rem' }} >
                    <div onClick={() => navigate(`/hcv-shortfall/${r.slug || r.code}`)} role="link" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate(`/hcv-shortfall/${r.slug || r.code}`)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'flex-start' }}>
                        <div style={{ fontWeight: 700, color: T.gray900 }}>{r.name}<div style={{ fontSize: '0.75rem', color: T.gray500, fontWeight: 500 }}>{r.city || r.code} · {r.code}</div></div>
                        <BucketChip bucket={r.bucket} />
                      </div>
                      <div style={{ fontSize: '0.8rem', color: T.gray600, marginTop: '0.4rem', lineHeight: 1.4 }}>{r.story}</div>
                      <div style={{ display: 'flex', gap: '0.9rem', marginTop: '0.5rem', fontSize: '0.75rem', color: T.gray500, flexWrap: 'wrap' }}>
                        <span><strong style={{ color: T.gray800 }}>{fmt.int(r.leasedLatest)}</strong> leased</span>
                        <span><strong style={{ color: T.gray800 }}>{fmt.pct(r.burnRatio)}</strong> of funding</span>
                        <span><strong style={{ color: T.gray800 }}>{fmt.months(r.reserveMonths)}</strong> mo reserves</span>
                        {r.zeroMonthLabel && <span>runs out <strong style={{ color: T.gray800 }}>{r.zeroMonthLabel}</strong></span>}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
                    <thead>
                      <tr>
                        <th style={th}>Housing authority</th>
                        <th style={th}>Status</th>
                        <th style={{ ...th, textAlign: 'right' }}>Leased / ACC</th>
                        <th style={{ ...th, textAlign: 'right' }} title="Trailing 3-month HAP ÷ monthly 2026 budget authority">Spend vs funding</th>
                        <th style={{ ...th, textAlign: 'right' }} title="Trailing 12 months HAP ÷ budget authority">Budget util. 12 mo</th>
                        <th style={{ ...th, textAlign: 'right' }}>Reserves</th>
                        <th style={{ ...th, textAlign: 'right' }}>Runs out</th>
                        <th style={th}>HUD status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r) => (
                        <tr key={r.code} onClick={() => navigate(`/hcv-shortfall/${r.slug || r.code}`)} style={{ cursor: 'pointer' }}
                          onMouseOver={(e) => { e.currentTarget.style.background = T.blue50; }} onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                          <td style={td}>
                            <div style={{ fontWeight: 700, color: T.gray900 }}>{r.name}</div>
                            <div style={{ fontSize: '0.74rem', color: T.gray500 }}>{r.city || ''}{r.city ? ' · ' : ''}{r.code}{r.mtw ? ' · MTW' : ''}</div>
                          </td>
                          <td style={td}><BucketChip bucket={r.bucket} /></td>
                          <td style={num}>{fmt.int(r.leasedLatest)} / {fmt.int(r.accUnits)}<div style={{ fontSize: '0.72rem', color: T.gray500 }}>{fmt.pct(r.unitUtil)}</div></td>
                          <td style={num}>{fmt.pct(r.burnRatio)}<div style={{ fontSize: '0.72rem', color: T.gray500 }}>{fmt.usdShort(r.burnMonthly)} / {fmt.usdShort(r.monthlyBa)} mo</div></td>
                          <td style={num}>{fmt.pct(r.budgetUtil12, 1)}</td>
                          <td style={num}>{fmt.usdShort(r.reservesNow)}<div style={{ fontSize: '0.72rem', color: T.gray500 }}>{fmt.months(r.reserveMonths)} months of HAP</div></td>
                          <td style={{ ...num, color: r.bucket === 'exhausted' ? BUCKETS.exhausted.color : r.bucket === 'shortfall' ? BUCKETS.shortfall.color : T.gray800, fontWeight: r.zeroMonthLabel || r.bucket === 'exhausted' ? 700 : 400 }}>{r.bucket === 'exhausted' ? 'Already out' : (r.zeroMonthLabel || (r.bucket === 'nodata' ? '—' : 'Not within 3 yrs'))}</td>
                          <td style={{ ...td, fontSize: '0.78rem', color: T.gray600 }}>{r.hudStatus || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            <Card style={{ marginTop: '1.25rem', fontSize: '0.8rem', color: T.gray600, lineHeight: 1.55 }}>
              <strong style={{ color: T.gray800 }}>How to read this.</strong> Source is HUD&apos;s Two-Year Tool workbook (HUDCAPS budget authority and reserves, VMS leasing and HAP, PIC attrition), the same numbers behind HUD&apos;s HCV Data Dashboard.
              Reserves start from HUD&apos;s reconciled 12/31 total (HUD-held plus excess cash) and move each month by budget authority minus HAP. Months HUD has reported use actual HAP; later months carry the trailing three-month burn forward with the agency&apos;s own per-unit-cost trend, and next year&apos;s funding is re-benchmarked on this year&apos;s spending times HUD&apos;s inflation factor at 99% proration.
              &ldquo;Runs out&rdquo; is the first month that balance goes negative. Everything here is an estimate from public aggregates; nothing touches tenant records. Definitions follow the <a href="https://www.hud.gov/sites/dfiles/PIH/documents/Dashboard-Data-Dictionary-FINAL.pdf" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: T.blue600 }}>HUD dashboard data dictionary</a>.
              {' '}Built by <a href="https://gethousingai.com" style={{ textDecoration: 'underline', color: T.blue600 }}>HousingAI</a>.
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
